import { useEffect, useRef, useState } from "react";
import { GO_BOARD_MOKA_SELF_PLAY_MAXIMUM_MOVE_COUNT, MOKA_MATCH_EVEN_WIN_PROBABILITY, MOKA_ROLLOUT_DEFAULT_GAME_COUNT } from "@/constants";
import { createMokaGameState, getMokaBlackWinProbability, initializeMokaEngine, playMokaMove } from "@/lib/moka-go-engine";
import { getMokaAreaScore } from "@/utils/get-moka-area-score";
import { getMokaFinalBlackWinProbability } from "@/utils/get-moka-final-black-win-probability";
import { isMokaGameOver } from "@/utils/is-moka-game-over";
const createMokaRolloutGame = gameIndex => ({
  blackWinProbability: MOKA_MATCH_EVEN_WIN_PROBABILITY,
  didReachMoveCap: false,
  gameState: createMokaGameState(),
  id: gameIndex,
  result: null,
  stones: []
});
const advanceMokaRolloutGame = async game => {
  const moveResult = await playMokaMove(game.gameState, Math.random());
  const nextGameState = moveResult.nextState;
  const didReachMoveCap = nextGameState.moveCount >= GO_BOARD_MOKA_SELF_PLAY_MAXIMUM_MOVE_COUNT;
  if (!isMokaGameOver(nextGameState)) {
    return {
      ...game,
      blackWinProbability: await getMokaBlackWinProbability(nextGameState),
      didReachMoveCap,
      gameState: nextGameState,
      stones: moveResult.stones
    };
  }
  const score = getMokaAreaScore(nextGameState);
  return {
    ...game,
    blackWinProbability: getMokaFinalBlackWinProbability(score),
    didReachMoveCap,
    gameState: nextGameState,
    result: score > 0 ? "black" : score < 0 ? "white" : "draw",
    stones: moveResult.stones
  };
};
const useMokaRollouts = () => {
  const activeRunIdReference = useRef(0);
  const [durationMs, setDurationMs] = useState(0);
  const [errorMessage, setErrorMessage] = useState(null);
  const [gameCount, setGameCount] = useState(MOKA_ROLLOUT_DEFAULT_GAME_COUNT);
  const [games, setGames] = useState([]);
  const [hoveredResult, setHoveredResult] = useState(null);
  const [selectedResult, setSelectedResult] = useState(null);
  const [status, setStatus] = useState("idle");
  const completedGames = games.filter(game => game.result !== null);
  useEffect(() => () => {
    activeRunIdReference.current += 1;
  }, []);
  const runRollouts = async () => {
    const runId = activeRunIdReference.current + 1;
    activeRunIdReference.current = runId;
    const startTime = performance.now();
    let activeGames = Array.from({
      length: gameCount
    }, (_, gameIndex) => createMokaRolloutGame(gameIndex));
    setDurationMs(0);
    setErrorMessage(null);
    setGames(activeGames);
    setHoveredResult(null);
    setSelectedResult(null);
    setStatus("loading");
    try {
      await initializeMokaEngine();
      if (activeRunIdReference.current !== runId) {
        return;
      }
      setStatus("running");
      while (activeGames.some(game => game.result === null)) {
        activeGames = await Promise.all(activeGames.map(game => game.result === null ? advanceMokaRolloutGame(game) : Promise.resolve(game)));
        if (activeRunIdReference.current !== runId) {
          return;
        }
        setDurationMs(performance.now() - startTime);
        setGames(activeGames);
      }
      setStatus("complete");
    } catch (error) {
      if (activeRunIdReference.current !== runId) {
        return;
      }
      setErrorMessage(error instanceof Error ? error.message : "Unable to run Moka rollouts.");
      setStatus("idle");
    }
  };
  return {
    completedGames,
    durationMs,
    errorMessage,
    gameCount,
    games,
    highlightedResult: hoveredResult ?? selectedResult,
    isRunning: status === "loading" || status === "running",
    runRollouts,
    selectedResult,
    setGameCount,
    setHoveredResult,
    setSelectedResult,
    status
  };
};
export { useMokaRollouts };